'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { isStaffRole } from '@/core/auth/staffRoles';

export function TrafficTracker() {
    const pathname = usePathname();
    const { data: session, status } = useSession();

    useEffect(() => {
        // Wait until session is resolved — otherwise this effect fires twice:
        // once with session=undefined (loading) and once after it resolves,
        // which would double-count every page view.
        if (status === 'loading') return;

        if (pathname?.startsWith('/admin')) return;

        // Exclude logged-in administrators (and other internal/staff accounts) from
        // distortion of traffic metrics
        if (session?.user && isStaffRole((session.user as any).role)) return;

        const trackVisit = async () => {
            try {
                const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                const deviceType = isMobile ? 'mobile' : 'desktop';

                // Unique visit tracking is per device type
                const storageKey = `shopstag_last_tracked_${deviceType}_date`;
                const todayStr = new Date().toISOString().split('T')[0]; // e.g. "2026-06-01"
                const lastTrackedDate = localStorage.getItem(storageKey);
                let isUnique = false;

                if (lastTrackedDate !== todayStr) {
                    localStorage.setItem(storageKey, todayStr);
                    isUnique = true;
                }

                // Call backend public endpoint
                await fetch('/api/track-visit', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        is_unique: isUnique,
                        path: pathname || '/',
                        device_type: deviceType,
                    }),
                });
            } catch (error) {
                console.error('Failed to register traffic analytics visit:', error);
            }
        };

        trackVisit();
    }, [pathname, status, session]);

    return null;
}

